import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Check, ListMinus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useEffect, useMemo } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { WithTooltip } from '@/components/with-tooltip';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Granularity } from './sales-chart';

interface GranularityFilterProps {
  fromDate: string | undefined;
  toDate: string | undefined;
  granularity: Granularity;
  setGranularity: (granularity: Granularity) => void;
}

export function GranularityFilter({
  fromDate,
  toDate,
  granularity,
  setGranularity,
}: GranularityFilterProps) {
  const { LL, locale } = useI18nContext();

  const diffInMinutes = Math.ceil(
    DateTime.fromISO(toDate ?? '')
      .diff(DateTime.fromISO(fromDate ?? ''), 'minute')
      .get('minute')
  );

  const granularityOptions = useMemo(
    () => [
      {
        label: LL.salesChart.granularity.hour(),
        value: 'hour',
        enabled: diffInMinutes <= 3 * 24 * 60,
      },
      {
        label: LL.salesChart.granularity.day(),
        value: 'day',
        enabled: diffInMinutes <= 120 * 24 * 60 && diffInMinutes > 24 * 60,
      },
      {
        label: LL.salesChart.granularity.week(),
        value: 'week',
        enabled:
          diffInMinutes <= 53 * 7 * 24 * 60 && diffInMinutes > 7 * 24 * 60,
      },
      {
        label: LL.salesChart.granularity.month(),
        value: 'month',
        enabled: diffInMinutes > 1 * 31 * 24 * 60,
      },
    ],
    [diffInMinutes, LL, locale]
  );

  useEffect(() => {
    // Ensure current granularity is still valid after date range change, if not, set to first enabled option
    if (granularityOptions.find((g) => g.value === granularity)?.enabled)
      return;

    const firstEnabled = granularityOptions.find((g) => g.enabled);
    if (!firstEnabled) return;
    setGranularity(firstEnabled.value as Granularity);
  }, [granularity, granularityOptions, setGranularity]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className={cn('justify-between')} variant="outline">
          <>
            <ListMinus />
            <p className="text-ellipsis overflow-hidden whitespace-nowrap">
              {granularityOptions?.find((g) => g.value === granularity)
                ?.label || ''}
            </p>
            <ChevronDown />
          </>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              {granularityOptions?.map((g) => (
                <WithTooltip
                  key={g.value}
                  disabled={g.enabled}
                  tooltip={LL.granularityNotAvailable()}
                >
                  <CommandItem
                    className="cursor-pointer"
                    disabled={!g.enabled}
                    onSelect={() => {
                      setGranularity(g.value as Granularity);
                    }}
                  >
                    {g.label}
                    <Check
                      className={cn(
                        'ml-auto',
                        granularity === g.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                </WithTooltip>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
